/**
 * Inventory commands — the cupboard's write path (PLAN §3 V1).
 *
 * Five commands, four of which are the create/update/delete/restore shape the
 * equipment and water-profile domains already established, so there is nothing
 * new to learn:
 *
 * | Command | Shape it follows |
 * | --- | --- |
 * | `inventory.add` | `batch.create` — a required `from` document, because the item *is* its identity and this package has no clock and no catalog lookup |
 * | `inventory.update` | `equipment.update` — a partial `changes`, inverted with the prior values of exactly those keys |
 * | `inventory.remove` | `equipment.delete` — inverted with the whole prior document |
 * | `inventory.restore` | `equipment.restore` — an upsert, triple duty (undo, exact inverse, sync push) |
 * | `inventory.adjustQuantity` | new: a **delta**, inverted by the opposite delta |
 *
 * The fifth is the interesting one. Stock moves; it is not re-declared. A delta
 * composes across devices where an absolute quantity would lose a movement, and
 * it is what a brew-day deduction and a shopping trip both are. See
 * {@link p.inventoryAdjustQuantityPayloadSchema} for why the reducer refuses to
 * clamp at zero rather than quietly breaking undo.
 *
 * There is no built-in/read-only rule here — every item in the cupboard is the
 * brewer's own — so no command in this file has an `invalid-state` refusal
 * except the one that protects the quantity floor.
 */

import { hasOneProvenance } from '../../document/common';
import { customIngredientMaker } from '../../document/custom-ingredient';
import type { InventoryItemDoc } from '../../document/inventory';
import { roundQuantity, roundUnitCost } from '../../document/inventory';
import type { WorkspaceState } from '../../document/workspace';
import { INVENTORY_SCHEMA_VERSION } from '../../version';
import { defineCommand, inverseCommand } from '../definition';
import { commandError } from '../envelope';
import { quoted } from '../format';
import { mergeDefined, priorValues } from '../helpers';
import * as p from '../payloads';

/** A copy of `state` with one inventory item replaced or added. */
function putItem(state: WorkspaceState, item: InventoryItemDoc): WorkspaceState {
  return { ...state, inventory: { ...state.inventory, [item.id]: item } };
}

/** Look an item up for a summary, tolerating its absence (summaries never throw). */
function itemName(state: WorkspaceState, itemId: string): string {
  return state.inventory[itemId]?.name ?? itemId;
}

/** `12 g`, `1.5 packs` — the way a quantity reads in a one-line summary. */
function quantity(value: number, kind: InventoryItemDoc['kind']): string {
  const unit = kind === 'yeast' ? 'packs' : 'g';
  return `${roundQuantity(value)} ${unit}`;
}

// ---------------------------------------------------------------------------
// Lifecycle
// ---------------------------------------------------------------------------

/** Add a lot to the cupboard from a fully-built document. */
export const inventoryAdd = defineCommand({
  type: 'inventory.add',
  payload: p.inventoryAddPayloadSchema,
  apply: (state, command) => {
    const { itemId, from, name } = command.payload;
    if (state.inventory[itemId] !== undefined) {
      return {
        ok: false,
        error: commandError('conflict', `An inventory item with id "${itemId}" already exists.`, {
          commandId: command.commandId,
          commandType: command.type,
        }),
      };
    }
    // A linked lot's label belongs to the ingredient it is a lot of (see the
    // header of `document/inventory.ts`): its name and maker are read off the
    // library here, and the payload's own spelling — including the optional
    // `name` override — is set aside. This is the *add* verb, where the inverse
    // is a whole-document remove, so filling behind the payload's back costs the
    // undo identity nothing (D-IL9). An id that does not resolve is kept as it
    // is: on a second device the `customIngredient.add` it names may simply not
    // have arrived yet, and the payload's spelling stands until it does.
    const linked =
      from.ingredientId === null ? undefined : state.customIngredients[from.ingredientId];
    const doc: InventoryItemDoc = {
      ...from,
      schemaVersion: INVENTORY_SCHEMA_VERSION,
      id: itemId,
      name: linked === undefined ? (name ?? from.name) : linked.name,
      maker: linked === undefined ? from.maker : customIngredientMaker(linked),
      quantity: roundQuantity(from.quantity),
      unitCost: from.unitCost === null ? null : roundUnitCost(from.unitCost),
      createdAt: command.timestamp,
      updatedAt: command.timestamp,
    };
    return { ok: true, state: putItem(state, doc) };
  },
  invert: (_state, command) =>
    inverseCommand(
      command,
      'inventory.remove',
      { itemId: command.payload.itemId },
      command.timestamp,
    ),
  summarize: (state, command) => {
    const { from, name } = command.payload;
    // Same resolution `apply` uses: a linked lot's name is the entry's, not
    // whatever the payload carried, so the undo stack reads what the document
    // actually ended up holding.
    const linked =
      from.ingredientId === null ? undefined : state.customIngredients[from.ingredientId];
    const label = linked === undefined ? (name ?? from.name) : linked.name;
    return `Stock ${quantity(from.quantity, from.kind)} of ${quoted(label)}`;
  },
});

/** Change fields of one stocked item. A present `null` clears a nullable field. */
export const inventoryUpdate = defineCommand({
  type: 'inventory.update',
  payload: p.inventoryUpdatePayloadSchema,
  apply: (state, command) => {
    const item = state.inventory[command.payload.itemId];
    if (item === undefined) {
      return {
        ok: false,
        error: commandError('not-found', `No inventory item with id "${command.payload.itemId}".`, {
          commandId: command.commandId,
          commandType: command.type,
        }),
      };
    }
    const merged = mergeDefined(item, command.payload.changes);
    if (!hasOneProvenance(merged)) {
      return {
        ok: false,
        error: commandError(
          'invalid-state',
          'A lot is catalog-backed or linked to one of your ingredients, never both; clear one before setting the other.',
          { commandId: command.commandId, commandType: command.type },
        ),
      };
    }
    return {
      ok: true,
      state: putItem(state, {
        ...merged,
        quantity: roundQuantity(merged.quantity),
        unitCost: merged.unitCost === null ? null : roundUnitCost(merged.unitCost),
        updatedAt: command.timestamp,
      }),
    };
  },
  invert: (state, command) => {
    const item = state.inventory[command.payload.itemId];
    return item === undefined
      ? null
      : inverseCommand(
          command,
          'inventory.update',
          { itemId: item.id, changes: priorValues(item, command.payload.changes) },
          item.updatedAt,
        );
  },
  summarize: (state, command) => {
    const keys = Object.keys(command.payload.changes).sort().join(', ');
    return `Update ${quoted(itemName(state, command.payload.itemId))}${
      keys === '' ? '' : ` (${keys})`
    }`;
  },
});

/** Remove a lot. Invertible: the inverse carries the whole prior document. */
export const inventoryRemove = defineCommand({
  type: 'inventory.remove',
  payload: p.inventoryRemovePayloadSchema,
  apply: (state, command) => {
    if (state.inventory[command.payload.itemId] === undefined) {
      return {
        ok: false,
        error: commandError('not-found', `No inventory item with id "${command.payload.itemId}".`, {
          commandId: command.commandId,
          commandType: command.type,
        }),
      };
    }
    const inventory = { ...state.inventory };
    delete inventory[command.payload.itemId];
    return { ok: true, state: { ...state, inventory } };
  },
  invert: (state, command) => {
    const item = state.inventory[command.payload.itemId];
    return item === undefined
      ? null
      : inverseCommand(command, 'inventory.restore', { item }, item.updatedAt);
  },
  summarize: (state, command) =>
    `Remove ${quoted(itemName(state, command.payload.itemId))} from inventory`,
});

/** Insert an item verbatim — an upsert, and the inverse of `inventory.remove`. */
export const inventoryRestore = defineCommand({
  type: 'inventory.restore',
  payload: p.inventoryRestorePayloadSchema,
  apply: (state, command) => ({ ok: true, state: putItem(state, command.payload.item) }),
  invert: (state, command) => {
    const prior = state.inventory[command.payload.item.id];
    return prior === undefined
      ? inverseCommand(
          command,
          'inventory.remove',
          { itemId: command.payload.item.id },
          command.timestamp,
        )
      : inverseCommand(command, 'inventory.restore', { item: prior }, prior.updatedAt);
  },
  summarize: (_state, command) => `Restore ${quoted(command.payload.item.name)} to inventory`,
});

// ---------------------------------------------------------------------------
// Movement
// ---------------------------------------------------------------------------

/**
 * Add to or take from a lot.
 *
 * The floor is enforced, not clamped: `quantity + delta < 0` comes back as
 * `invalid-state` with the two numbers named, because clamping would leave the
 * inverse (`−delta`) restoring a quantity that never existed. A caller that
 * means "take whatever is left" computes that delta itself.
 */
export const inventoryAdjustQuantity = defineCommand({
  type: 'inventory.adjustQuantity',
  payload: p.inventoryAdjustQuantityPayloadSchema,
  apply: (state, command) => {
    const item = state.inventory[command.payload.itemId];
    if (item === undefined) {
      return {
        ok: false,
        error: commandError('not-found', `No inventory item with id "${command.payload.itemId}".`, {
          commandId: command.commandId,
          commandType: command.type,
        }),
      };
    }
    const delta = roundQuantity(command.payload.delta);
    const next = roundQuantity(item.quantity + delta);
    if (next < 0) {
      return {
        ok: false,
        error: commandError(
          'invalid-state',
          `${quoted(item.name)} holds ${quantity(item.quantity, item.kind)}; ` +
            `you don't have ${quantity(-delta, item.kind)} left to take.`,
          { commandId: command.commandId, commandType: command.type },
        ),
      };
    }
    return {
      ok: true,
      state: putItem(state, { ...item, quantity: next, updatedAt: command.timestamp }),
    };
  },
  invert: (state, command) => {
    const item = state.inventory[command.payload.itemId];
    return item === undefined
      ? null
      : inverseCommand(
          command,
          'inventory.adjustQuantity',
          {
            itemId: item.id,
            delta: -roundQuantity(command.payload.delta),
            // Carried through so the undo entry says what it is undoing rather
            // than reading as an unexplained restock.
            reason: command.payload.reason ?? null,
          },
          item.updatedAt,
        );
  },
  summarize: (state, command) => {
    const item = state.inventory[command.payload.itemId];
    const name = quoted(item?.name ?? command.payload.itemId);
    const delta = roundQuantity(command.payload.delta);
    const amount = quantity(Math.abs(delta), item?.kind ?? 'hop');
    const movement = delta < 0 ? `Take ${amount} of ${name}` : `Add ${amount} to ${name}`;
    const reason = command.payload.reason ?? null;
    return reason === null ? movement : `${movement} — ${reason}`;
  },
});
